import { FC, forwardRef, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { FaIcon, Modal, useMessageEventSubscription } from '@fe-learner-common';

const QuizIframe = forwardRef<
  HTMLIFrameElement,
  { src: string; title: string }
>(({ src, title }, ref) => {
  return (
    <iframe
      ref={ref}
      src={src}
      title={title}
      className="lf:h-full lf:w-full lf:rounded-b-2xl"
    />
  );
});
QuizIframe.displayName = 'QuizIframe';

type IframeMessageEvent =
  | 'startAlewebSpinner'
  | 'stopAlewebSpinner'
  | 'closeIframeModal';

export const QuizSurveyModal = ({
  container,
  src,
  title,
  Trigger,
  onClose,
}: {
  container: HTMLElement;
  src: string;
  title: string;
  Trigger: FC<Dialog.DialogTriggerProps>;
  onClose?: () => void;
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const time = useRef(Date.now());
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const close = () => {
    setOpen(false);
    onClose?.();
  };

  useMessageEventSubscription<IframeMessageEvent>({
    enabled: true,
    ref: iframeRef,
    listener: (event) => {
      switch (event.data) {
        case 'startAlewebSpinner':
          setIsLoading(true);
          break;
        case 'stopAlewebSpinner':
          setTimeout(
            () => {
              setIsLoading(false);
            },
            Math.max(0, 1000 - (Date.now() - time.current)),
          );

          break;
        case 'closeIframeModal':
          close();
          break;
      }
    },
  });

  return (
    <Modal
      container={container}
      Trigger={Trigger}
      open={open}
      onOpenChange={(newValue) => {
        if (newValue) {
          setIsLoading(true);
          time.current = Date.now();
        }

        setOpen(newValue);
      }}
      TitleContent={<h1 className="lf:text-title-md">{title}</h1>}
      onClose={() => {
        close();
      }}
      className="lf:inset-x-0 lf:z-[1010] lf:flex lf:h-full lf:w-full lf:md:h-[90%] lf:md:w-[90%] lf:md:max-w-[1200px] "
      overlayClassName="lf:z-[1010] lf:bg-black/50 lf:backdrop-blur-xs"
    >
      <div className="lf:relative lf:h-full lf:flex-1 lf:bg-neutral-100">
        {isLoading && (
          <div className="lf:absolute lf:inset-0 lf:flex lf:items-center lf:justify-center lf:rounded-b-2xl lf:bg-neutral-0">
            <FaIcon
              icon={faSpinner}
              className="lf:animate-spin lf:text-neutral-500 lf:duration-1000"
              width={40}
              height={40}
            />
          </div>
        )}
        <QuizIframe ref={iframeRef} src={src} title={title} />
      </div>
    </Modal>
  );
};
